import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Outlet, Navigate, Link } from 'react-router-dom';
import { LanguageProvider } from './src/components/LanguageContext';
import { LanguageUtils } from './lib/languageUtils';
import { AppProviders } from './src/components/AppProviders';
import { AdminProviders } from './src/components/AdminProviders';
import { DonationsProvider } from './src/contexts/DonationsContext';
import { YearProvider } from './src/contexts/YearContext';
import { LanguageValidator, RedirectToLanguage } from './src/components/LanguageValidator';
import ErrorBoundary from './src/components/ErrorBoundary';
import Header from './src/components/ui/Header';
import { useLanguage } from './src/hooks/useLanguage';
import { usePageTitle } from './src/hooks/usePageTitle';
import { MaintenanceModeGate } from './src/components/MaintenanceModeGate';
import { SkeletonLayout } from './src/components/ui/Skeleton';
import { AppVersion } from './src/components/ui/AppVersion';

// Lazy loaded components
const HomePage = lazy(() => import('./src/pages/HomePage'));
const DonationPage = lazy(() => import('./src/pages/DonationPage'));
const AdminPage = lazy(() => import('./src/pages/AdminPage'));
const CalendarPage = lazy(() => import('./src/pages/CalendarPage'));
const CalendarEmbedPage = lazy(() => import('./src/pages/CalendarEmbedPage'));
const PrivacyPolicyPage = lazy(() => import('./src/pages/PrivacyPolicyPage'));
const TermsPage = lazy(() => import('./src/pages/TermsPage'));
const AdminChatbotWidget = lazy(() => import('./src/components/admin/AdminChatbotWidget'));
const PwaInstallBanner = lazy(() => import('./src/components/ui/PwaInstallBanner'));

// Fallback loading component
const LoadingFallback = () => {
    const { t } = useLanguage();
    return <SkeletonLayout label={t('common.loading')} className="flex min-h-screen items-center justify-center" />;
};

/**
 * Last resort inside the language-prefixed route tree. Without it an unmatched
 * path under a valid language prefix renders `null` — React mounts, the outer
 * providers render, and the user gets a blank white page with no error and no
 * way back. `/en/index.html` is the case that surfaced it: Lighthouse CI serves
 * dist as static files and requests `/index.html` by filename, the outer
 * catch-all prepends the language, and nothing inside LanguageRoutes matched.
 * Any typo'd path (`/en/evets`) hit the same dead end.
 *
 * Redirecting rather than rendering a 404 page matches what the outer route
 * tree already does with unknown paths via RedirectToLanguage. Reads the
 * language from context rather than resolving a relative path, so the target is
 * unambiguous from anywhere in the splat.
 */
const UnmatchedRouteRedirect: React.FC = () => {
    const { language } = useLanguage();
    return <Navigate to={`/${language}`} replace />;
};

// Component to handle language routing.
// Exported for tests — it is the piece that decides what an unmatched
// language-prefixed path renders, and that is only meaningful when mounted
// under a `/:lang/*` parent the way App mounts it.
export const LanguageRoutes: React.FC = () => {
    return (
        <MaintenanceModeGate>
            <Suspense fallback={<LoadingFallback />}>
                <Routes>
                    <Route path="/" element={<AppLayout />}>
                        {/* Keep the old booking URL as a compatibility redirect; events now have one canonical page. */}
                        <Route path="book" element={<Navigate to="../events" replace />} />
                        {/* Keep the old donate URL as a compatibility redirect for already-sent links/bookmarks. */}
                        <Route path="donate" element={<Navigate to="../offerings" replace />} />
                        {/* Home, offerings and admin all need donation data (Home renders a
                            "Featured Donations" section); sharing one DonationsProvider here
                            means navigating between them doesn't unmount/remount it and refetch. */}
                        <Route element={<DonationsProvider><Outlet /></DonationsProvider>}>
                            <Route index element={<HomePage />} />
                            <Route path="offerings" element={<DonationPage />} />
                            <Route path="admin" element={<AdminProviders><AdminPage /></AdminProviders>} />
                            <Route path="admin/:tab" element={<AdminProviders><AdminPage /></AdminProviders>} />
                        </Route>
                        <Route path="events" element={<CalendarPage />} />
                        <Route path="events/:eventId" element={<CalendarPage />} />
                        <Route path="calendar" element={<CalendarPage />} />
                        <Route path="calendar/:eventId" element={<CalendarPage />} />
                        <Route path="privacy" element={<PrivacyPolicyPage />} />
                        <Route path="terms" element={<TermsPage />} />
                    </Route>
                    {/* Calendar embed route without layout */}
                    <Route path="calendar-embed" element={<CalendarEmbedPage />} />
                    {/* Events/booking embed route without layout - reuses CalendarPage directly since it already excludes Header/footer */}
                    <Route path="events-embed" element={<CalendarPage />} />
                    <Route path="events-embed/:eventId" element={<CalendarPage />} />
                    {/* Offerings/donation embed route without layout - reuses DonationPage directly since it already excludes Header/footer */}
                    <Route path="offerings-embed" element={<DonationsProvider><DonationPage /></DonationsProvider>} />
                    {/* Must stay last: anything unmatched above would otherwise render null. */}
                    <Route path="*" element={<UnmatchedRouteRedirect />} />
                </Routes>
            </Suspense>
        </MaintenanceModeGate>
    );
};

const AppLayout: React.FC = () => {
    const { t, language } = useLanguage();
    usePageTitle();
    return (
        <div className="flex flex-col min-h-screen">
            {/* WCAG 2.1 2.4.1 Bypass Blocks (Level A). The header nav repeats on
                every route, so a keyboard user must be able to jump past it.
                Hidden until focused via `sr-only focus:not-sr-only`, so it costs
                nothing visually but is the first thing Tab reaches.

                `tabIndex={-1}` on the target is required, not decorative:
                without it the browser scrolls to <main> but leaves focus on the
                link, so the next Tab returns to the nav the user just skipped. */}
            <a
                href="#main-content"
                className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-[100] focus:rounded-md focus:bg-card focus:px-4 focus:py-2 focus:text-foreground focus:shadow-lg focus:outline-none focus:ring-2 focus:ring-ring"
            >
                {t('accessibility.skipToContent')}
            </a>
            <Header />
            <main id="main-content" tabIndex={-1} className="grow focus:outline-none">
                <Suspense fallback={<LoadingFallback />}>
                    <Outlet />
                </Suspense>
            </main>
            <footer className="border-t border-border bg-card text-muted-foreground py-6 mt-12">
                <div className="container mx-auto flex flex-col items-center gap-2 text-center text-sm">
                    <nav className="flex items-center gap-4">
                        <Link to={LanguageUtils.getLocalizedPath(language, 'privacy')} className="hover:text-foreground hover:underline">
                            {t('footer.privacyPolicy')}
                        </Link>
                        <Link to={LanguageUtils.getLocalizedPath(language, 'terms')} className="hover:text-foreground hover:underline">
                            {t('footer.termsOfConditions')}
                        </Link>
                    </nav>
                    <span>{t('footer.copyright', { year: new Date().getFullYear() })}</span>
                    <AppVersion />
                </div>
            </footer>
            <Suspense fallback={null}>
                <PwaInstallBanner />
            </Suspense>
        </div>
    );
};

const App: React.FC = () => {
    return (
        <ErrorBoundary>
            <YearProvider>
                <AppProviders>
                    {/* No `future` prop: on react-router-dom 7 the v6 opt-in flags
                        (v7_startTransition, v7_relativeSplatPath, …) are the
                        default behaviour and the prop no longer accepts them. */}
                    <BrowserRouter>
                        <Routes>
                            {/* Routes with language prefix - validate language parameter */}
                            <Route path="/:lang/*" element={
                                <LanguageValidator>
                                    <LanguageProvider>
                                        <LanguageRoutes />
                                    </LanguageProvider>
                                </LanguageValidator>
                            } />

                            {/* Routes without language prefix - redirect to default language */}
                            <Route path="/" element={<RedirectToLanguage />} />

                            {/* Special route for calendar embed (no language prefix needed) */}
                            <Route path="/calendar-embed" element={
                                <LanguageProvider>
                                    <MaintenanceModeGate>
                                        <Suspense fallback={<LoadingFallback />}>
                                            <CalendarEmbedPage />
                                        </Suspense>
                                    </MaintenanceModeGate>
                                </LanguageProvider>
                            } />

                            {/* Special route for events/booking embed (no language prefix needed) */}
                            <Route path="/events-embed" element={
                                <LanguageProvider>
                                    <MaintenanceModeGate>
                                        <Suspense fallback={<LoadingFallback />}>
                                            <CalendarPage />
                                        </Suspense>
                                    </MaintenanceModeGate>
                                </LanguageProvider>
                            } />
                            <Route path="/events-embed/:eventId" element={
                                <LanguageProvider>
                                    <MaintenanceModeGate>
                                        <Suspense fallback={<LoadingFallback />}>
                                            <CalendarPage />
                                        </Suspense>
                                    </MaintenanceModeGate>
                                </LanguageProvider>
                            } />

                            {/* Special route for offerings/donation embed (no language prefix needed) */}
                            <Route path="/offerings-embed" element={
                                <LanguageProvider>
                                    <MaintenanceModeGate>
                                        <Suspense fallback={<LoadingFallback />}>
                                            <DonationsProvider>
                                                <DonationPage />
                                            </DonationsProvider>
                                        </Suspense>
                                    </MaintenanceModeGate>
                                </LanguageProvider>
                            } />

                            {/* Catch-all route for any other paths - redirect to default language */}
                            <Route path="*" element={<RedirectToLanguage />} />
                        </Routes>
                        <AdminChatbotWidget />
                    </BrowserRouter>
                </AppProviders>
            </YearProvider>
        </ErrorBoundary>
    );
};

export default App;
